import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { GoogleDrivePicker } from "@/components/GoogleDrivePicker";
import { StudentPicker } from "@/components/ManualMatch";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, CheckCircle2, XCircle, Brain, Link2, Users, RefreshCw, FileSearch } from "lucide-react";

function UnmatchedRow({ batchId, nama, onAssigned }) {
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const assign = async () => {
    if (!picked) { toast.error("Pilih akun siswa dulu"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/partner/assign-assessment", { batch_id: batchId, nama, user_id: picked.id });
      toast.success(`"${nama}" → ${data.assigned_to}`);
      setDone(true);
      onAssigned && onAssigned();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mencocokkan"); }
    setBusy(false);
  };

  if (done) return (
    <div className="flex items-center gap-2 py-2 text-sm text-[#4A6B53]"><CheckCircle2 className="h-4 w-4" /> <b>{nama}</b> dicocokkan ke {picked?.name}</div>
  );
  return (
    <div className="grid sm:grid-cols-[1fr_1.4fr_auto] gap-2 items-center py-2 border-b border-[#F0ECE4] last:border-0">
      <div className="text-sm font-medium text-[#1A1C1A]">{nama}</div>
      <StudentPicker onPick={setPicked} />
      <button onClick={assign} disabled={busy || !picked} data-testid={`assess-assign-btn-${nama}`}
        className="px-4 py-2 rounded-full bg-[#1A1C1A] text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-40 hover:bg-black transition-colors">
        <Link2 className="h-3.5 w-3.5" /> Cocokkan
      </button>
    </div>
  );
}

export default function AdminAsesmenUpload() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [bLoading, setBLoading] = useState(true);
  const [repairing, setRepairing] = useState(false);

  const repairKecerdasan = async () => {
    setRepairing(true);
    try {
      const { data } = await api.post("/admin/reports/repair-kecerdasan");
      toast.success(`${data.reports_updated} laporan diperbaiki skornya`);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal memperbaiki skor"); }
    setRepairing(false);
  };

  const loadBatches = async () => {
    setBLoading(true);
    try {
      const { data } = await api.get("/admin/partner/assessment-batches");
      setBatches(data.batches || []);
    } catch { /* noop */ }
    setBLoading(false);
  };
  useEffect(() => { loadBatches(); }, []);

  const submit = async () => {
    if (!files.length) { toast.error("Pilih satu atau beberapa PDF asesmen iTi"); return; }
    setLoading(true); setResult(null);
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/admin/partner/upload-assessments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const jobId = data.job_id;
      let tries = 0;
      const poll = async () => {
        tries++;
        try {
          const { data: st } = await api.get(`/admin/partner/assessment-status/${jobId}`);
          if (st.status === "done") { setResult(st); toast.success(`${st.matched} asesmen tersalur`); setLoading(false); loadBatches(); }
          else if (st.status === "error") { toast.error("Gagal memproses PDF"); setLoading(false); }
          else if (tries < 200) { setTimeout(poll, 3000); }
          else { toast.error("Proses terlalu lama, coba lagi"); setLoading(false); }
        } catch { setLoading(false); toast.error("Gagal memeriksa status"); }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengunggah PDF");
      setLoading(false);
    }
  };

  const pending = batches.filter((b) => (b.unmatched_count || 0) > 0);

  const [dbgFile, setDbgFile] = useState(null);
  const [dbgLoading, setDbgLoading] = useState(false);
  const [dbg, setDbg] = useState(null);

  const runDebug = async () => {
    if (!dbgFile) { toast.error("Pilih satu PDF untuk didiagnosa"); return; }
    setDbgLoading(true); setDbg(null);
    const fd = new FormData();
    fd.append("file", dbgFile);
    try {
      const { data } = await api.post("/admin/partner/debug-extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDbg(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal diagnosa"); }
    setDbgLoading(false);
  };

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto">
        <h1 className="font-heading font-black text-3xl text-[#1A1C1A] mb-1 flex items-center gap-2"><Brain className="h-7 w-7 text-[#4A6B53]" /> Upload Asesmen Mitra (Massal)</h1>
        <p className="text-[#5C5F5C] mb-6">Unggah banyak PDF laporan asesmen iTi sekaligus. Sistem membaca nama tiap laporan & menyalurkannya otomatis ke akun siswa yang cocok.</p>

        <div className="clay-card p-6 space-y-4">
          <div>
            <label className="text-sm font-semibold text-[#1A1C1A] block mb-1">File PDF (bisa banyak sekaligus)</label>
            <input type="file" accept="application/pdf" multiple onChange={(e) => setFiles(e.target.files ? [...e.target.files] : [])} data-testid="assess-file-input"
              className="w-full text-sm text-[#5C5F5C] file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#1A1C1A] file:text-white file:text-sm" />
            {files.length > 0 && <p className="text-xs text-[#5C5F5C] mt-1">{files.length} file dipilih</p>}
            <div className="mt-2"><GoogleDrivePicker multiple onPicked={(fs) => setFiles((prev) => [...prev, ...fs])} /></div>
          </div>
          <button onClick={submit} disabled={loading} data-testid="assess-upload-btn"
            className="px-6 py-3 rounded-full bg-[#E25E3E] text-white font-medium hover:bg-[#C94F31] transition-colors inline-flex items-center gap-2 disabled:opacity-60">
            <Upload className="h-4 w-4" /> {loading ? "Memproses PDF…" : "Upload & Salurkan"}
          </button>
          <p className="text-xs text-[#9C9A94]">Memproses banyak PDF butuh waktu (±1 menit/berkas). Biarkan halaman terbuka hingga selesai.</p>
        </div>

        <div className="clay-card p-6 mt-6" data-testid="assess-debug-panel">
          <h2 className="font-heading font-bold text-lg text-[#1A1C1A] mb-1 flex items-center gap-2"><FileSearch className="h-5 w-5 text-[#3E6B8A]" /> Diagnosa Ekstraksi PDF</h2>
          <p className="text-sm text-[#5C5F5C] mb-3">Bila ekstraksi gagal, unggah satu PDF di sini untuk melihat teks mentah yang terbaca, nama hasil deteksi otomatis, dan hasil AI. Data tidak disimpan — hanya untuk pemeriksaan.</p>
          <input type="file" accept="application/pdf" onChange={(e) => { setDbgFile(e.target.files?.[0] || null); setDbg(null); }} data-testid="assess-debug-input"
            className="w-full text-sm text-[#5C5F5C] file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#3E6B8A] file:text-white file:text-sm" />
          <button onClick={runDebug} disabled={dbgLoading || !dbgFile} data-testid="assess-debug-btn"
            className="mt-3 px-5 py-2.5 rounded-full bg-[#3E6B8A] text-white font-medium hover:bg-[#345a75] transition-colors inline-flex items-center gap-2 disabled:opacity-50">
            <FileSearch className="h-4 w-4" /> {dbgLoading ? "Menganalisa…" : "Diagnosa"}
          </button>

          <div className="mt-4 pt-4 border-t border-[#F0ECE4]">
            <p className="text-sm text-[#5C5F5C] mb-2">Jika skor <b>7 Kecerdasan Bawaan</b> tampil 0 pada laporan lama, klik untuk menghitung ulang skor dari level yang tersimpan.</p>
            <button onClick={repairKecerdasan} disabled={repairing} data-testid="repair-kecerdasan-btn"
              className="px-5 py-2.5 rounded-full bg-[#4A6B53] text-white font-medium hover:bg-[#3d5a45] transition-colors inline-flex items-center gap-2 disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${repairing ? "animate-spin" : ""}`} /> {repairing ? "Memperbaiki…" : "Perbaiki Skor Kecerdasan"}
            </button>
          </div>

          {dbg && (
            <div className="mt-4 space-y-3" data-testid="assess-debug-result">
              <div className="grid sm:grid-cols-2 gap-2 text-sm">
                <div className="rounded-lg bg-[#F7F4EE] px-3 py-2"><span className="text-[#9C9A94]">Nama (deteksi otomatis):</span> <b className="text-[#1A1C1A]" data-testid="dbg-manual-name">{dbg.manual_name || "— tak terdeteksi —"}</b></div>
                <div className="rounded-lg bg-[#F7F4EE] px-3 py-2"><span className="text-[#9C9A94]">Nama (AI):</span> <b className="text-[#1A1C1A]" data-testid="dbg-ai-name">{dbg.ai_ok ? (dbg.ai_nama_siswa || "— kosong —") : "GAGAL"}</b></div>
                <div className="rounded-lg bg-[#F7F4EE] px-3 py-2"><span className="text-[#9C9A94]">Tipe kepribadian (AI):</span> <b className="text-[#1A1C1A]">{dbg.ai_ok ? (dbg.ai_tipe_kepribadian?.nama || dbg.ai_tipe_kepribadian?.kode || "— kosong —") : "-"}</b></div>
                <div className="rounded-lg bg-[#F7F4EE] px-3 py-2"><span className="text-[#9C9A94]">Panjang teks terbaca:</span> <b className="text-[#1A1C1A]">{dbg.raw_len} karakter</b></div>
                <div className="rounded-lg bg-[#F7F4EE] px-3 py-2"><span className="text-[#9C9A94]">Metode ekstraksi:</span> <b className="text-[#1A1C1A]" data-testid="dbg-method">{dbg.method === "vision" ? "Vision/OCR gambar" : dbg.method === "file" ? "Teks (file)" : "-"}</b>{typeof dbg.page_images_rendered === "number" ? <span className="text-[#9C9A94]"> · {dbg.page_images_rendered} halaman dirender</span> : null}</div>
              </div>
              {dbg.method === "vision" && (
                <p className="text-sm text-[#4A6B53] flex items-center gap-1.5" data-testid="dbg-vision-note"><CheckCircle2 className="h-4 w-4" /> PDF ini dibaca via OCR gambar (Vision) karena teks langsung tak memadai.</p>
              )}
              {dbg.is_scanned_likely && (
                <p className="text-sm text-[#D9381E] flex items-center gap-1.5" data-testid="dbg-scanned-warn"><XCircle className="h-4 w-4" /> PDF ini kemungkinan hasil scan/gambar (teks hampir kosong) — perlu OCR, AI mungkin gagal membacanya.</p>
              )}
              {!dbg.ai_ok && dbg.ai_error && (
                <p className="text-sm text-[#D9381E]" data-testid="dbg-ai-error">Error AI: {dbg.ai_error}</p>
              )}
              <div>
                <p className="text-xs font-semibold text-[#1A1C1A] mb-1">Teks mentah yang terbaca (cuplikan):</p>
                <pre className="text-[11px] leading-relaxed bg-[#1A1C1A] text-[#E8E4DC] rounded-xl p-3 overflow-auto max-h-72 whitespace-pre-wrap" data-testid="dbg-raw-text">{dbg.raw_text_sample || "(kosong — tidak ada teks yang bisa dibaca dari PDF)"}</pre>
              </div>
            </div>
          )}
        </div>

        {result && (
          <div className="clay-card p-6 mt-6" data-testid="assess-result">
            <h2 className="font-heading font-bold text-lg text-[#1A1C1A] mb-3">Ringkasan Upload</h2>
            <div className="flex gap-4 mb-3">
              <div className="flex items-center gap-2 text-[#4A6B53]"><CheckCircle2 className="h-5 w-5" /> <b>{result.matched}</b> tersalur</div>
              <div className="flex items-center gap-2 text-[#D9381E]"><XCircle className="h-5 w-5" /> <b>{result.unmatched?.length || 0}</b> belum cocok</div>
              <div className="text-[#5C5F5C]">Total: {result.total}</div>
            </div>
            {result.unmatched?.length > 0 && (
              <p className="text-xs text-[#5C5F5C]">Nama yang belum cocok bisa dicocokkan manual di panel bawah.</p>
            )}
          </div>
        )}

        <div className="clay-card p-6 mt-6" data-testid="assess-manual-panel">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-heading font-bold text-lg text-[#1A1C1A] flex items-center gap-2"><Users className="h-5 w-5 text-[#E25E3E]" /> Pencocokan Asesmen Manual</h2>
            <button onClick={loadBatches} data-testid="assess-refresh-btn" className="text-sm text-[#5C5F5C] inline-flex items-center gap-1 hover:text-[#1A1C1A]">
              <RefreshCw className={`h-3.5 w-3.5 ${bLoading ? "animate-spin" : ""}`} /> Muat ulang
            </button>
          </div>
          <p className="text-sm text-[#5C5F5C] mb-4">Laporan asesmen yang namanya tak otomatis cocok. Pilih akun siswa yang benar untuk menyalurkan hasilnya ke Profilku.</p>
          {bLoading && <p className="text-sm text-[#9C9A94]">Memuat…</p>}
          {!bLoading && pending.length === 0 && <p className="text-sm text-[#4A6B53]" data-testid="assess-no-unmatched">Semua asesmen sudah tercocokkan. 🎉</p>}
          <div className="space-y-5">
            {pending.map((b) => (
              <div key={b.id} data-testid={`assess-batch-${b.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-[#1A1C1A]">{b.uploaded_at ? new Date(b.uploaded_at).toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "Batch"}</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#D9381E]/10 text-[#D9381E]">{b.unmatched_count} belum cocok</span>
                </div>
                <div className="rounded-xl border border-[#E5E0D8] px-4">
                  {(b.unmatched_list || []).map((nama, i) => (
                    <UnmatchedRow key={i} batchId={b.id} nama={nama} onAssigned={loadBatches} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
